import { styled } from "@mui/material/styles";
import { AcquireBookDataGrid } from "@/components/DataGrid";
import {
  type GridSelectionModel,
  type GridColDef,
  type GridRenderCellParams,
  type GridColumnGroupingModel,
} from "@mui/x-data-grid-pro";
import { type RealEstateTitleSearchDataGridRow } from "../types";
import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";
import { feedDetailAPI } from "../api";
import { type ToolbarProps, TitleSearchToolbar } from "./TitleSearchToolbar";
import { useApiClient } from "@/hooks/useApiClient";
import { HttpStatusCode } from "axios";
import { toast } from "react-toastify";
import { REAL_ESTATE_TYPE_MAP, ID_DELIMITER } from "../routes/TitleSearch";
import Link from "@mui/material/Link";
import { useFeatureFlags } from "@/configs/featureFlag";
import { formatNumberWithComma } from "@/utils/formatNumber";

// landColumns, buildingAndKubunBuildingColumnsのどちらかを指定して表示する
// dataGridに表示する列の種類を定義する
// 注意: この並びを変えてもdataGridの列の表示順序は変わりません。変えるにはcolDef配列の並びを変える必要があります
const landColumns = [
  "action",
  "realEstateType",
  "landType",
  "prefecture",
  "syozai",
  "stationAndWalk",
  "areaUsePurposeType",
  "latestReceptionDateAndReason",
  "estimateAcreage",
  "price",
  "yousekiRatio",
  "kenpeiRatio",
  "parkingLotName",
  "parkingLotAccommodation",
  "totalParkingLotArea",
];

// 建物、区分建物のカラム表示は共通
const buildingAndKubunBuildingColumns = [
  "action",
  "realEstateType",
  "prefecture",
  "syozai",
  "buildingDetailAddress",
  "buildingType",
  "propertyName",
  "stationAndWalk",
  "areaUsePurposeType",
  "yousekiRatio",
  "kenpeiRatio",
  "latestReceptionDateAndReason",
  "price",
  "buildingAge",
  "buildingBuildDate",
  "buildingVariant",
  "groundFloors",
  "undergroundFloors",
  "buildingTotalUnits",
  "parkingSpaces",
  "landArea",
  "buildingArea",
  "totalFloorArea",
  "kaokuNumber",
  "buildingMinLayout",
  "buildingMaxLayout",
  "buildingMainDirection",
  "buildingExclusiveAreaMin",
  "buildingExclusiveAreaMax",
  "buildingNeighborhoodOtherInformation",
  "hotelName",
  "dormitoryName",
  "industrialFacilityName",
  "hotelCategory",
  "hotelEstimateAcreage",
  "hotelRoomCount",
];

const HOTEL_DETAIL_TITLES = new Set([
  "ホテル名",
  "ホテル形態",
  "客室数",
  "ホテル推計地積(㎡)",
]);

const DORMITORY_DETAIL_TITLES = new Set(["寮・社宅名"]);
const INDUSTRIAL_FACILITY_DETAIL_TITLES = new Set(["産業施設名"]);

/** グルーピングモデルから指定グループのフィールド名一覧を取得する */
const getGroupFieldNames = (
  model: GridColumnGroupingModel,
  groupId: string
): string[] =>
  (model.find((group) => group.groupId === groupId)?.children ?? []).flatMap(
    (child) => ("field" in child ? [child.field] : [])
  );

// グルーピングに関する定義、土地、建物、区分建物で別々の定義をしている
// rowのデータ内容によって使い分ける
const landColumnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: "basicInfo",
    headerName: "基本情報",
    headerAlign: "center",
    children: [
      { field: "action" },
      { field: "realEstateType" },
      { field: "landType" },
      { field: "prefecture" },
      { field: "syozai" },
    ],
  },
  {
    groupId: "areaPrice",
    headerName: "面積・価格",
    headerAlign: "center",
    children: [{ field: "estimateAcreage" }, { field: "price" }],
  },
  {
    groupId: "locationInfo",
    headerName: "立地情報",
    headerAlign: "center",
    children: [{ field: "stationAndWalk" }],
  },
  {
    groupId: "receptionInfo",
    headerName: "不動産の権利異動",
    headerAlign: "center",
    children: [{ field: "latestReceptionDateAndReason" }],
  },
  {
    groupId: "areaUsePurpose",
    headerName: "用途地域",
    headerAlign: "center",
    children: [
      { field: "areaUsePurposeType" },
      { field: "kenpeiRatio" },
      { field: "yousekiRatio" },
    ],
  },
  {
    groupId: "parkingLot",
    headerName: "駐車場",
    headerAlign: "center",
    children: [
      { field: "parkingLotName" },
      { field: "parkingLotAccommodation" },
      { field: "totalParkingLotArea" },
    ],
  },
];

// 建物、区分建物グループ
const buildingAndKubunBuildingColumnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: "basicInfo",
    headerName: "基本情報",
    headerAlign: "center",
    children: [
      { field: "action" },
      { field: "realEstateType" },
      { field: "buildingType" },
      { field: "prefecture" },
      { field: "syozai" },
      { field: "buildingDetailAddress" },
      { field: "propertyName" },
    ],
  },
  {
    groupId: "areaPrice",
    headerName: "面積・価格",
    headerAlign: "center",
    children: [{ field: "price" }],
  },
  {
    groupId: "locationInfo",
    headerName: "立地情報",
    headerAlign: "center",
    children: [{ field: "stationAndWalk" }],
  },
  {
    groupId: "receptionInfo",
    headerName: "不動産の権利異動",
    headerAlign: "center",
    children: [{ field: "latestReceptionDateAndReason" }],
  },
  {
    groupId: "areaUsePurpose",
    headerName: "用途地域",
    headerAlign: "center",
    children: [
      { field: "areaUsePurposeType" },
      { field: "kenpeiRatio" },
      { field: "yousekiRatio" },
    ],
  },
  {
    groupId: "buildingProperties",
    headerName: "建物情報",
    headerAlign: "center",
    children: [
      { field: "buildingAge" },
      { field: "buildingBuildDate" },
      { field: "buildingVariant" },
      { field: "groundFloors" },
      { field: "undergroundFloors" },
      { field: "buildingTotalUnits" },
      { field: "parkingSpaces" },
      { field: "landArea" },
      { field: "buildingArea" },
      { field: "totalFloorArea" },
      { field: "kaokuNumber" },
      { field: "buildingMinLayout" },
      { field: "buildingMaxLayout" },
      { field: "buildingMainDirection" },
      { field: "buildingExclusiveAreaMin" },
      { field: "buildingExclusiveAreaMax" },
      { field: "buildingNeighborhoodOtherInformation" }, // colDefに定義がないので表示はされない
    ],
  },
  {
    groupId: "hotelProperties",
    headerName: "ホテル",
    headerAlign: "center",
    children: [
      { field: "hotelName" },
      { field: "hotelCategory" },
      { field: "hotelEstimateAcreage" },
      { field: "hotelRoomCount" },
    ],
  },
  {
    groupId: "dormitoryProperties",
    headerName: "寮・社宅",
    headerAlign: "center",
    children: [{ field: "dormitoryName" }],
  },
  {
    groupId: "industrialFacilityProperties",
    headerName: "工場/倉庫",
    headerAlign: "center",
    children: [{ field: "industrialFacilityName" }],
  },
];

interface FeedGridProps {
  loading: boolean;
  rows: RealEstateTitleSearchDataGridRow[];
  setRows: Dispatch<SetStateAction<RealEstateTitleSearchDataGridRow[]>>;
  setOpen: (value: boolean) => void;
  setDetailData: (
    value: Array<{ title: string; value: string | string[] }> | null
  ) => void;
  displayHasTrade?: boolean;
  page: number;
  pageSize: number;
  changePage: (page: number) => void;
  changePageSize: (pageSize: number) => void;
  rowsPerPageOptions: number[];
}

const StyledDataGrid = styled(AcquireBookDataGrid)`
  // テーブルのセルをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-cell:focus-within,
  & .MuiDataGrid-cell:focus {
    outline: none !important;
  }

  // テーブルのカラムヘッダをクリックしても青い枠が表示されないようにする
  & .MuiDataGrid-columnHeader:focus-within,
  & .MuiDataGrid-columnHeader:focus {
    outline: none !important;
  }

  // テーブルヘッダーの背景色を設定
  .MuiDataGrid-columnHeaders {
    line-height: 1.5em !important;
  }

  // テーブルタイトルを太字に
  .MuiDataGrid-columnHeaderTitle {
    font-weight: 700;
  }
`;

/**
 * row配列から不動産種別を判断してbooleanのオブジェクトで返す
 *
 * @param {RealEstateTitleSearchDataGridRow[]} rows - 物件種別から検索する画面の検索結果の配列
 * @returns {{hasLand: boolean, hasBuilding: boolean}}
 *          土地なら hasLandがtrue、建物、区分建物ならhasBuildingがtrue
 *          当てはまらない場合は、hasLandとhasBuildingがfalseとなります
 */
const identifyRealEstateType = (
  rows: RealEstateTitleSearchDataGridRow[]
): { hasLand: boolean; hasBuilding: boolean } => {
  for (const row of rows) {
    if (row.realEstateType === REAL_ESTATE_TYPE_MAP.land.label) {
      return { hasLand: true, hasBuilding: false };
    } else if (
      row.realEstateType === REAL_ESTATE_TYPE_MAP.building.label ||
      row.realEstateType === REAL_ESTATE_TYPE_MAP.kubunBuilding.label
    ) {
      return { hasLand: false, hasBuilding: true };
    }
  }

  // どれにも当てはまらない場合
  return { hasLand: false, hasBuilding: false };
};

export const FeedGrid: React.FC<FeedGridProps> = ({
  loading,
  rows,
  setRows,
  setOpen,
  setDetailData,
  displayHasTrade = false,
  page,
  pageSize,
  changePage,
  changePageSize,
  rowsPerPageOptions,
}) => {
  const { hotelSearch, dormitorySearch, industrialFacilitySearch } =
    useFeatureFlags();
  const hotelSearchEnabled = Boolean(hotelSearch);
  const dormitorySearchEnabled = Boolean(dormitorySearch);
  const industrialFacilitySearchEnabled = Boolean(industrialFacilitySearch);
  const apiClient = useApiClient().apiClient;
  const hasHotelSpecificData = useMemo(() => {
    return rows.some(
      (row) =>
        row.buildingType === "ホテル" ||
        Boolean(row.hotelName) ||
        Boolean(row.hotelCategory) ||
        row.hotelEstimateAcreage != null ||
        row.hotelRoomCount != null
    );
  }, [rows]);
  const hasDormitorySpecificData = useMemo(() => {
    return rows.some(
      (row) => row.buildingType === "寮・社宅" || Boolean(row.dormitoryName)
    );
  }, [rows]);
  const hasIndustrialFacilitySpecificData = useMemo(() => {
    return rows.some(
      (row) =>
        row.buildingType === "工場/倉庫" || Boolean(row.industrialFacilityName)
    );
  }, [rows]);

  const clickActionButton = async (id: string): Promise<void> => {
    setDetailData(null);
    setOpen(true);

    const splitId = id.split(ID_DELIMITER);
    const realEstateType =
      Object.values(REAL_ESTATE_TYPE_MAP).find(
        (item) => item.label === splitId[0]
      )?.value ?? "";
    const bookId = Number(splitId[1]);

    const result = await feedDetailAPI(apiClient, {
      id: bookId,
      realEstateType,
    });

    if (result?.status === HttpStatusCode.Unauthorized) {
      toast.info("再度検索ボタンを押下してください");
      return;
    } else if (result?.status === HttpStatusCode.BadRequest) {
      toast.error(result?.error);
      return;
    } else if (result?.status === HttpStatusCode.InternalServerError) {
      toast.error("予期せぬエラーが発生しました");
      return;
    }

    if (result?.data?.land != null) {
      setDetailData(result?.data?.land);
    } else if (result?.data?.building != null) {
      const clickedRow = rows.find((row) => row.id === id);
      const isHotelRow =
        clickedRow?.buildingType === "ホテル" ||
        Boolean(clickedRow?.hotelName) ||
        Boolean(clickedRow?.hotelCategory) ||
        clickedRow?.hotelEstimateAcreage != null ||
        clickedRow?.hotelRoomCount != null;
      const isDormitoryRow =
        clickedRow?.buildingType === "寮・社宅" ||
        Boolean(clickedRow?.dormitoryName);
      const isIndustrialFacilityRow =
        clickedRow?.buildingType === "工場/倉庫" ||
        Boolean(clickedRow?.industrialFacilityName);
      const allowHotelDetail = hotelSearchEnabled && isHotelRow;
      const allowDormitoryDetail = dormitorySearchEnabled && isDormitoryRow;
      const allowIndustrialFacilityDetail =
        industrialFacilitySearchEnabled && isIndustrialFacilityRow;

      // NOTE: バックエンドの /real-estate-title/{id} の building[].title 値に依存
      // title が変更された場合は、この除外対象も合わせて更新が必要
      setDetailData(
        result.data.building
          .filter((item) => {
            const title = String(item.title);

            if (!allowHotelDetail && HOTEL_DETAIL_TITLES.has(title)) {
              return false;
            }

            if (!allowDormitoryDetail && DORMITORY_DETAIL_TITLES.has(title)) {
              return false;
            }

            if (
              !allowIndustrialFacilityDetail &&
              INDUSTRIAL_FACILITY_DETAIL_TITLES.has(title)
            ) {
              return false;
            }

            return true;
          })
          .map((item) =>
            INDUSTRIAL_FACILITY_DETAIL_TITLES.has(String(item.title))
              ? { ...item, title: "工場/倉庫名" }
              : item
          )
      );
    }
  };

  const colDef: GridColDef[] = [
    {
      field: "action",
      headerName: "",
      minWidth: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: ({ id }: GridRenderCellParams) => {
        return (
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              void (async () => {
                await clickActionButton(String(id));
              })();
            }}
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              color: "primary.main",
            }}
          >
            詳細を開く
          </Link>
        );
      },
    },
    {
      field: "realEstateType",
      headerName: "種別",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "landType",
      headerName: "土地種別",
      width: 100,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "buildingType",
      headerName: "建物種別",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "prefecture",
      headerName: "都道府県",
      width: 90,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "syozai",
      headerName: "所在",
      width: 300,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "buildingDetailAddress",
      headerName: "住居表示",
      width: 300,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "propertyName",
      headerName: "建物名",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const rowType = (params.row as RealEstateTitleSearchDataGridRow)
          .realEstateType;
        if (
          rowType === REAL_ESTATE_TYPE_MAP.building.value ||
          rowType === REAL_ESTATE_TYPE_MAP.kubunBuilding.value
        ) {
          const value = params.value as string | null;
          return value ?? "－";
        }
        return null;
      },
    },
    {
      field: "estimateAcreage",
      headerName: "推計地積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null
          ? `${value.toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "－";
      },
    },
    {
      field: "price",
      headerName: "公示価格(円/㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null
          ? `${value.toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "－";
      },
    },
    {
      field: "stationAndWalk",
      headerName: "最寄駅および徒歩距離・時間",
      width: 350,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "areaUsePurposeType",
      headerName: "用途地域",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "yousekiRatio",
      headerName: "容積率(%)",
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${formatNumberWithComma(value)}` : "－";
      },
    },
    {
      field: "kenpeiRatio",
      headerName: "建ぺい率(%)",
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${formatNumberWithComma(value)}` : "－";
      },
    },
    {
      field: "latestReceptionDateAndReason",
      headerName: "登記原因・法務局受付日",
      width: 300,
      sortable: false,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "buildingAge",
      headerName: "築年数",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${formatNumberWithComma(value)}` : "－";
      },
    },
    {
      field: "buildingBuildDate",
      headerName: "築年月",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        if (value) {
          const date = new Date(value);
          return `${date.getFullYear()}年${date.getMonth() + 1}月`;
        }
        return "－";
      },
    },
    {
      field: "buildingVariant",
      headerName: "構造",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value != null ? value : "－";
      },
    },
    {
      field: "groundFloors",
      headerName: "地上階数",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${formatNumberWithComma(value)}` : "－";
      },
    },
    {
      field: "undergroundFloors",
      headerName: "地下階数",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${formatNumberWithComma(value)}` : "－";
      },
    },
    {
      field: "buildingTotalUnits",
      headerName: "総戸数",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${formatNumberWithComma(value)}` : "－";
      },
    },
    {
      field: "parkingSpaces",
      headerName: "駐車場数",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${formatNumberWithComma(value)}` : "－";
      },
    },
    {
      field: "landArea",
      headerName: "土地面積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null
          ? `${value.toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "－";
      },
    },
    {
      field: "buildingArea",
      headerName: "建築面積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null
          ? `${value.toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "－";
      },
    },
    {
      field: "totalFloorArea",
      headerName: "延べ床面積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null
          ? `${value.toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "－";
      },
    },
    {
      field: "parkingLotName",
      headerName: "駐車場名",
      width: 350,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;

        if (!value) return "－";

        return (
          <div
            style={{
              width: "100%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      field: "parkingLotAccommodation",
      headerName: "駐車場 収容台数",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;

        if (!value) return "－";

        return (
          <div
            style={{
              width: "100%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {formatNumberWithComma(value)}
          </div>
        );
      },
    },
    {
      field: "totalParkingLotArea",
      headerName: "駐車場 推計地積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${value.toLocaleString("ja-JP")}` : "－";
      },
    },
    {
      field: "kaokuNumber",
      headerName: "家屋番号数",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? formatNumberWithComma(value) : "－";
      },
    },
    {
      field: "buildingMinLayout",
      headerName: "最小間取り",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "buildingMaxLayout",
      headerName: "最大間取り",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "buildingMainDirection",
      headerName: "主方位",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "buildingExclusiveAreaMin",
      headerName: "最小専有面積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null
          ? `${value.toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "－";
      },
    },
    {
      field: "buildingExclusiveAreaMax",
      headerName: "最大専有面積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null
          ? `${value.toLocaleString("ja-JP", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`
          : "－";
      },
    },
    {
      field: "hotelName",
      headerName: "ホテル名",
      width: 350,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;

        if (!value) return "－";

        return (
          <div
            style={{
              width: "100%",
              textAlign: "center",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </div>
        );
      },
    },
    {
      field: "dormitoryName",
      headerName: "施設名",
      width: 350,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "industrialFacilityName",
      headerName: "工場/倉庫名",
      width: 350,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "hotelCategory",
      headerName: "ホテル形態",
      width: 200,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as string | null;
        return value ?? "－";
      },
    },
    {
      field: "hotelEstimateAcreage",
      headerName: "ホテル 推計地積(㎡)",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? `${value.toLocaleString("ja-JP")}` : "－";
      },
    },
    {
      field: "hotelRoomCount",
      headerName: "ホテル 部屋数",
      width: 200,
      sortable: true,
      align: "center",
      headerAlign: "center",
      renderCell: (params: GridRenderCellParams) => {
        const value = params.value as number | null;
        return value != null ? formatNumberWithComma(value) : "－";
      },
    },

    // NOTE: 2025/01/27 hotfixにて地番または家屋番号を非表示に変更
    // {
    //   field: "chibanKaokuNo",
    //   headerName: "地番または家屋番号",
    //   width: 220,
    //   sortable: false,
    //   align: "center",
    //   headerAlign: "center",
    // },
  ];

  if (displayHasTrade) {
    colDef.splice(2, 0, {
      field: "hasTrade",
      headerName: "売買登記あり",
      width: 120,
      sortable: false,
      align: "center",
      headerAlign: "center",
    });
  }

  const handleRowSelectionChange = useCallback(
    (newSelectionModel: GridSelectionModel) => {
      setRows((prevRows) =>
        prevRows.map((row) => ({
          ...row,
          selected: newSelectionModel.includes(row.id),
        }))
      );
    },
    [setRows]
  );

  const dataGridPaginationProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    onPageChange: changePage,
    onPageSizeChange: changePageSize,
  };

  const toolbarProps: ToolbarProps = {
    page,
    pageSize,
    rowsPerPageOptions,
    changePage,
    changePageSize,
    allCount: rows.length,
  };

  // rowに含まれる不動産種別が土地か（建物、区分建物）かどちらかを判定する
  const realEstateType = useMemo(() => {
    return identifyRealEstateType(rows);
  }, [rows]);

  // 表示するカラムの種類をrowsの内容を見て、
  // 土地(landColumns)、建物or区分建物(buildingAndKubunBuildingColumns)のどちらかを選んで返す
  const displayedColumns = useMemo(() => {
    const { hasLand, hasBuilding } = realEstateType;
    const hotelColumnFields = getGroupFieldNames(
      buildingAndKubunBuildingColumnGroupingModel,
      "hotelProperties"
    );
    const dormitoryColumnFields = getGroupFieldNames(
      buildingAndKubunBuildingColumnGroupingModel,
      "dormitoryProperties"
    );
    const industrialFacilityColumnFields = getGroupFieldNames(
      buildingAndKubunBuildingColumnGroupingModel,
      "industrialFacilityProperties"
    );

    return colDef.filter((col) => {
      // ホテル実データがない場合はホテルカラムを除外
      if (
        !(hotelSearchEnabled && hasHotelSpecificData) &&
        hotelColumnFields.includes(col.field)
      ) {
        return false;
      }
      if (
        !(dormitorySearchEnabled && hasDormitorySpecificData) &&
        dormitoryColumnFields.includes(col.field)
      ) {
        return false;
      }
      if (
        !(
          industrialFacilitySearchEnabled && hasIndustrialFacilitySpecificData
        ) &&
        industrialFacilityColumnFields.includes(col.field)
      ) {
        return false;
      }
      if (hasBuilding && buildingAndKubunBuildingColumns.includes(col.field)) {
        return true;
      }
      if (hasLand && landColumns.includes(col.field)) return true;

      return false;
    });
  }, [
    colDef,
    realEstateType,
    hotelSearchEnabled,
    dormitorySearchEnabled,
    industrialFacilitySearchEnabled,
    hasHotelSpecificData,
    hasDormitorySpecificData,
    hasIndustrialFacilitySpecificData,
  ]);

  // rowの内容は土地のみの場合と建物の場合と、区分建物の場合、さらに建物と区分建物が混じって返される4パターンがあり得る。
  // その内容によってグルーピングのモデルを変更する
  const displayedColumnGroupingModel = useMemo((): GridColumnGroupingModel => {
    const { hasLand, hasBuilding } = realEstateType;

    if (hasBuilding) {
      return buildingAndKubunBuildingColumnGroupingModel.filter((group) => {
        if (group.groupId === "hotelProperties") {
          return hotelSearchEnabled && hasHotelSpecificData;
        }
        if (group.groupId === "dormitoryProperties") {
          return dormitorySearchEnabled && hasDormitorySpecificData;
        }
        if (group.groupId === "industrialFacilityProperties") {
          return (
            industrialFacilitySearchEnabled && hasIndustrialFacilitySpecificData
          );
        }
        return true;
      });
    }
    if (hasLand) {
      return landColumnGroupingModel;
    }
    return [];
  }, [
    realEstateType,
    hotelSearchEnabled,
    dormitorySearchEnabled,
    industrialFacilitySearchEnabled,
    hasHotelSpecificData,
    hasDormitorySpecificData,
    hasIndustrialFacilitySpecificData,
  ]);

  return (
    <StyledDataGrid
      stickyHeader={true}
      autoHeight
      checkboxSelection
      disableColumnMenu
      disableSelectionOnClick={true}
      loading={loading}
      rows={rows}
      columns={displayedColumns}
      columnGroupingModel={displayedColumnGroupingModel}
      experimentalFeatures={{ columnGrouping: true }}
      disableColumnReorder
      onSelectionModelChange={handleRowSelectionChange}
      initialState={{
        sorting: {
          sortModel: [{ field: "requestedAt", sort: "desc" }],
        },
      }}
      {...dataGridPaginationProps}
      components={{
        Toolbar: TitleSearchToolbar,
      }}
      componentsProps={{
        toolbar: {
          ...toolbarProps,
        },
      }}
    />
  );
};
